import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * LookupManager — minimal CRUD UI for simple {name, active} lookup collections.
 * Used in Settings for Contact Modes, Departments, Case Types, Branches.
 * `perm` (optional) gates create/edit/delete; omit to always show.
 */
export default function LookupManager({ title, endpoint, testId, extraField, perm }) {
  const { hasPerm } = useAuth();
  const canManage = perm ? hasPerm(perm) : true;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", active: true, ...(extraField ? { [extraField.key]: "" } : {}) });

  const load = () => api.get(endpoint).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [endpoint]);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", active: true, ...(extraField ? { [extraField.key]: "" } : {}) });
    setOpen(true);
  };
  const startEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, active: it.active, ...(extraField ? { [extraField.key]: it[extraField.key] || "" } : {}) });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try {
      if (editing) {
        await api.patch(`${endpoint}/${editing.id}`, form);
        toast.success(`${title} updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title} added`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`${endpoint}/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  return (
    <div data-testid={`lookup-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{items.length} entr{items.length === 1 ? "y" : "ies"}</p>
        </div>
        {canManage && (
          <Button size="sm" onClick={startCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`add-${testId}`}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add
          </Button>
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow className="text-xs uppercase tracking-wider text-slate-500">
            <TableHead>Name</TableHead>
            {extraField && <TableHead>{extraField.label}</TableHead>}
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} className="hover:bg-slate-50">
              <TableCell className="text-sm font-medium text-slate-800">{it.name}</TableCell>
              {extraField && <TableCell className="text-sm text-slate-600 font-mono">{it[extraField.key] || "—"}</TableCell>}
              <TableCell>
                <Badge variant="outline" className={`text-xs ${it.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                  {it.active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {canManage ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`edit-${testId}-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(it)} className="text-rose-600" data-testid={`delete-${testId}-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-slate-300">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow><TableCell colSpan={extraField ? 4 : 3} className="text-center text-sm text-slate-400 py-8">No entries</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={`${testId}-name-input`}
                autoFocus
              />
            </div>
            {extraField && (
              <div className="space-y-1.5">
                <Label>{extraField.label}</Label>
                <Input
                  value={form[extraField.key]}
                  onChange={(e) => setForm({ ...form, [extraField.key]: e.target.value })}
                  placeholder={extraField.placeholder}
                />
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`${testId}-submit`}>
              {editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
